import React, { useEffect, useState } from 'react'
import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonSpinner,
  IonToggle,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { getEKGImportSelector } from '../selectors'
import { useTranslation } from 'react-i18next'
import '../page/EKGImportMain.page.css'
import './ImportEvents.page.css'
import {
  getEventsAndReportsResults,
  setEventsListResults,
  setReportsListResults,
  setSelectedEvents,
  setSelectedReports,
} from '../store/ekgImport.actions'
import { IToggleState } from '../store/ekgImport.types'
import { chevronBack, chevronForward } from 'ionicons/icons'
import EventItem from '../eventItem/EventItem.component'
import Logger from '../../../services/logger.service'
import { getVitalOptionsSelector } from '../../options/selectors'

interface IImportEventsProps {
  onPrevious: () => void
  onNext: () => void
  onCancel: () => void
}

interface IImportEventsState {
  toggleState: IToggleState
  importAllStatus: boolean
  useImportByDefault: boolean
}

const initialState: IImportEventsState = {
  toggleState: null,
  importAllStatus: false,
  useImportByDefault: false,
}

const ImportEvents = (props: IImportEventsProps) => {
  const { onPrevious, onNext, onCancel } = props

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { eventsList, selectedEvents, loadedEvents, isLoading } = useSelector(getEKGImportSelector)

  const [state, setState] = useState<IImportEventsState>(initialState)
  const { toggleState, importAllStatus, useImportByDefault } = state
  const { temperatureMethodOptions } = useSelector(getVitalOptionsSelector)

  const onMount = () => {
    Logger.info('ECGImport: The user is on the event selection screen')
    dispatch(getEventsAndReportsResults())
  }

  useEffect(() => {
    onMount()
  }, [])

  const setUseImportByDefault = () => {
    setState((prevState) => ({
      ...prevState,
      useImportByDefault: true,
    }))
  }

  useEffect(() => {
    if (!loadedEvents) setUseImportByDefault()
  }, [loadedEvents])

  const fillToggleState = () => {
    const newToggleState = {
      ...Object.fromEntries(
        eventsList.map((event) => {
          const isChecked = useImportByDefault
            ? event.importByDefault
            : selectedEvents.some((selectedEvent) => selectedEvent.id === event.id)

          return [event.id, isChecked]
        })
      ),
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
      useImportByDefault: false,
    }))
  }

  useEffect(() => {
    if (eventsList.length) fillToggleState()
  }, [eventsList])

  const checkForAllImported = () => {
    const allImported = Object.values(toggleState as Record<string, boolean>).every((toggleStatus) => toggleStatus)
    const noneImported = Object.values(toggleState as Record<string, boolean>).some((toggleStatus) => !toggleStatus)

    if (allImported) {
      setState((prevState) => ({
        ...prevState,
        importAllStatus: true,
      }))
    } else if (noneImported) {
      setState((prevState) => ({
        ...prevState,
        importAllStatus: false,
      }))
    }
  }

  useEffect(() => {
    if (toggleState === null || !Object.keys(toggleState).length) return

    checkForAllImported()
  }, [toggleState])

  const onToggleEvent = (id: string) => {
    if (!toggleState) return

    const isChecked = !toggleState[id]
    const newToggleState = {
      ...toggleState,
      [id]: isChecked,
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
    }))
  }

  const onToggleImportAll = () => {
    if (!toggleState) return

    const newImportAllStatus = !importAllStatus
    const toggleIds = Object.keys(toggleState)

    const newToggleState = {
      ...Object.fromEntries(toggleIds.map((k) => [k, newImportAllStatus])),
    }

    setState((prevState) => ({
      ...prevState,
      toggleState: newToggleState,
      importAllStatus: newImportAllStatus,
    }))
  }

  const handlePrevious = () => {
    dispatch(setEventsListResults([]))
    dispatch(setReportsListResults([]))
    dispatch(setSelectedReports([]))
    onPrevious()
  }

  const handleNext = () => {
    if (toggleState) {
      const checkedEvents = eventsList.filter((event) => {
        return toggleState[event.id]
      })

      dispatch(setSelectedEvents(checkedEvents))
    }

    onNext()
  }

  const renderLoader = () => {
    return (
      <div>
        <IonSpinner data-testid='Spinner' className='pots-loader' />
      </div>
    )
  }

  const renderFooter = () => {
    const nextMessage =
      toggleState && Object.values(toggleState).some((isChecked) => isChecked)
        ? 'common.buttons.Next'
        : 'common.buttons.Skip'

    return (
      <IonFooter className='ekg-footer'>
        <IonButton className='ekg-footer-button' onClick={onCancel}>
          {t('common.buttons.Cancel')}
        </IonButton>
        <div className='ekg-footer-button-group'>
          <IonButton className='ekg-footer-button' onClick={handlePrevious}>
            <IonIcon className='ekg-footer-button-chevron' icon={chevronBack} color={'#6fa5d6'} />
            {t('common.buttons.Back')}
          </IonButton>
          <IonButton className='ekg-footer-button-alt' onClick={handleNext}>
            {t(nextMessage)}
            <IonIcon className='ekg-footer-button-chevron' icon={chevronForward} color={'#6fa5d6'} />
          </IonButton>
        </div>
      </IonFooter>
    )
  }

  const renderTableHeader = () => {
    return (
      <>
        <IonItem className='event-header-item'>
          <div className='event-item-main'>
            <IonToggle slot='start' checked={importAllStatus} onClick={onToggleImportAll} />
            <IonGrid className='event-item-grid header'>
              <IonRow>
                <IonCol className='event-time'>
                  <IonLabel>
                    <h3>{t('ekg.time')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-description'>
                  <IonLabel>
                    <h3>{t('ekg.description')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-heartRate'>
                  <IonLabel>
                    <h3>{t('ekg.hr')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-systolicBP'>
                  <IonLabel>
                    <h3>{t('ekg.SBP')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-diastolicBP'>
                  <IonLabel>
                    <h3>{t('ekg.DBP')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-map'>
                  <IonLabel>
                    <h3>{t('ekg.MAP')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-spo2'>
                  <IonLabel>
                    <h3>{t('ekg.SpO2')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-etco2'>
                  <IonLabel>
                    <h3>{t('ekg.EtCo2')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-resp'>
                  <IonLabel>
                    <h3>{t('ekg.Resp')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-spco'>
                  <IonLabel>
                    <h3>{t('ekg.SpCo')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-spmet'>
                  <IonLabel>
                    <h3>{t('ekg.SpMet')}</h3>
                  </IonLabel>
                </IonCol>
                <IonCol className='event-temp'>
                  <IonLabel>
                    <h3>{t('ekg.Temp')}</h3>
                  </IonLabel>
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        </IonItem>
        <div className='event-header-hr' />
      </>
    )
  }

  return (
    <>
      <IonContent fullscreen>
        <IonList lines='full'>
          {renderTableHeader()}
          {!!eventsList.length && !isLoading && (
            <>
              {eventsList.map((event, i) => {
                const isChecked = toggleState ? toggleState[event.id] : false

                return (
                  <EventItem
                    event={event}
                    key={i}
                    showToggle
                    isChecked={isChecked}
                    handleToggle={onToggleEvent}
                    temperatureMethodOptions={temperatureMethodOptions}
                  />
                )
              })}
            </>
          )}
        </IonList>
        {isLoading && renderLoader()}
      </IonContent>
      {renderFooter()}
    </>
  )
}

export default ImportEvents
